import { get } from 'svelte/store'
import { max_upload_file_size } from '../stores'
import { validateFileSize } from './font_file_upload'

/**
 * Validates font sizes and categorizes fonts
 * @param {Array} fontsToUpload - Fonts that need uploading
 * @param {Array} selectedFonts - All selected fonts
 * @param {Array} droppedFontFiles - Files dropped by user
 * @returns {Promise<Object>} Object with validFonts, oversizedFonts, alreadyUploaded
 */
export async function validateFontSizes(fontsToUpload, selectedFonts, droppedFontFiles) {
  if (!fontsToUpload || fontsToUpload.length === 0) {
    return {
      validFonts: [],
      oversizedFonts: [],
      alreadyUploaded: selectedFonts.filter(font => font.uploaded) || []
    }
  }

  // Get fonts that are already uploaded
  const alreadyUploaded = selectedFonts.filter(font => font.uploaded) || []

  const oversized = []
  const valid = []

  // Check dropped font files
  const droppedFilesMap = Object.fromEntries(droppedFontFiles.map(file => [file.name, file]))
  
  // Check local fonts that need to be queried
  const fontsNeedingLocalQuery = fontsToUpload.filter(font => !font.file_name)
  let localFontsData = []
  
  if (fontsNeedingLocalQuery.length > 0) {
    try {
      const allLocalFonts = await self.queryLocalFonts()
      const neededPostscriptNames = new Set(fontsNeedingLocalQuery.map(f => f.postscript_name))
      localFontsData = allLocalFonts.filter(fontData => neededPostscriptNames.has(fontData.postscriptName))
    } catch (error) {
      console.error('Error querying local fonts for size validation:', error)
    }
  }

  // Validate each font that needs uploading
  for (const font of fontsToUpload) {
    const sizeValidation = await validateSingleFont(font, droppedFilesMap, localFontsData)
    
    if (sizeValidation.isValid) {
      valid.push(font)
    } else if (sizeValidation.isOversized) {
      oversized.push({
        ...font,
        file_size: sizeValidation.fileSize,
        file_name: sizeValidation.fileName || font.postscript_name
      })
    } else {
      // If we can't determine size, assume it's valid
      valid.push(font)
    }
  }

  return {
    validFonts: valid,
    oversizedFonts: oversized,
    alreadyUploaded
  }
}

/**
 * Validates a single font's size
 */
async function validateSingleFont(font, droppedFilesMap, localFontsData) {
  let fileSize = 0
  let fileName = ''
  
  if (font.file_name && font.file_name in droppedFilesMap) {
    // Dropped font file
    const file = droppedFilesMap[font.file_name]
    fileSize = file.size
    fileName = file.name
  } else {
    // Local font - need to get blob size
    const localFont = localFontsData.find(f => f.postscriptName === font.postscript_name)
    if (localFont) {
      try {
        const blob = await localFont.blob()
        fileSize = blob.size
        fileName = `${font.postscript_name}.ttf` // Approximate filename
      } catch (error) {
        console.warn('Could not get blob size for local font:', font.postscript_name)
      }
    }
  }

  if (fileSize > 0) {
    const isValid = validateFileSize({ size: fileSize, maxSize: get(max_upload_file_size) })
    return {
      isValid,
      isOversized: !isValid,
      fileSize,
      fileName
    }
  }

  return { isValid: true, isOversized: false, fileSize: 0, fileName: '' }
}

/**
 * Prepares font entries for upload
 * @param {Array} validFonts - Fonts that passed validation
 * @param {Array} droppedFontFiles - Files dropped by user
 * @returns {Promise<Array>} Array of font entries ready for upload
 */
export async function prepareFontEntries(validFonts, droppedFontFiles) {
  const droppedFilesMap = Object.fromEntries(droppedFontFiles.map(file => [file.name, file]))
  let fontEntriesToUpload = []

  // Prepare entries for dropped fonts
  validFonts.forEach(font => {
    if (font.file_name && font.file_name in droppedFilesMap && !font.uploaded) {
      const file = droppedFilesMap[font.file_name]
      fontEntriesToUpload.push({
        file: file,
        metadata: {
          postscript_name: font.postscript_name,
          display_name: font.display_name,
        }
      })
    }
  })

  // Prepare entries for local fonts
  const fontsNeedingLocalUpload = validFonts.filter(font => !font.uploaded && !font.file_name)
  if (fontsNeedingLocalUpload.length > 0) {
    const localEntries = await prepareLocalFontEntries(fontsNeedingLocalUpload)
    fontEntriesToUpload = [...fontEntriesToUpload, ...localEntries]
  }

  return fontEntriesToUpload
}

/**
 * Prepares local font entries for upload
 */
async function prepareLocalFontEntries(fontsNeedingLocalUpload) {
  try {
    const allLocalFonts = await self.queryLocalFonts()
    const neededPostscriptNames = new Set(fontsNeedingLocalUpload.map(f => f.postscript_name))
    const localFontsData = allLocalFonts.filter(fontData => neededPostscriptNames.has(fontData.postscriptName))

    const entries = []
    for (const fontData of localFontsData) {
      const selectedFontInfo = fontsNeedingLocalUpload.find(f => f.postscript_name === fontData.postscriptName)
      if (selectedFontInfo) {
        const blob = await fontData.blob()
        let format = 'ttf' // Default
        if (blob.type === 'font/otf') format = 'otf'
        else if (blob.type === 'font/ttf') format = 'ttf'
        
        const filename = `${sanitizeFilename(fontData.postscriptName)}.${format}`
        
        entries.push({
          file: new File([blob], filename, { type: blob.type || 'font/' + format }),
          metadata: {
            postscript_name: fontData.postscriptName,
            display_name: fontData.fullName || fontData.postscriptName,
          }
        })
      }
    }
    return entries
  } catch (error) {
    console.error('Error preparing local fonts for upload:', error)
    throw new Error('Failed to prepare local fonts for upload: ' + error.message)
  }
}

/**
 * Simple filename sanitization
 */
function sanitizeFilename(filename) {
  return filename.replace(/[^a-z0-9]/gi, '_').toLowerCase()
}